import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../utils/ConfigUtils';

// RBAC Types
interface Permission {
  id: string;
  name: string;
  code: string;
  full_code: string;
  category: string;
  application_code: string;
  application_name: string;
}

interface RBACData {
  user_id: string;
  permission_codes: string[];
  role_codes: string[];
  all_permissions: Permission[];
  applications: any[];
  tenant: {
    id: string;
    name: string;
    level: string;
    code: string;
  };
  summary: {
    access_level: string;
    tenant_level: string;
    is_admin: boolean;
    is_active: boolean;
    total_applications: number;
    total_permissions: number;
    total_roles: number;
  };
}

interface RBACContextType {
  rbacData: RBACData | null;
  loading: boolean;
  hasPermission: (permissionCode: string) => boolean;
  hasRole: (roleCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  canAccess: (feature: string) => boolean;
  refreshRBAC: () => Promise<void>;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

// Permission mappings for easy feature access checking
const FEATURE_PERMISSIONS: Record<string, string[]> = {
  dashboard: ['ADM_VIEW_DASHBOARD'],
  tenants: ['ADM_VIEW_TENANTS'],
  tenants_create: ['ADM_CREATE_TENANTS'],
  tenants_edit: ['ADM_EDIT_TENANTS'],
  tenants_delete: ['ADM_DELETE_TENANTS'],
  users: ['ADM_VIEW_USERS'],
  users_create: ['ADM_CREATE_USERS'],
  users_edit: ['ADM_EDIT_USERS'],
  users_delete: ['ADM_DELETE_USERS'],
  products: ['ADM_VIEW_PRODUCTS'],
  products_manage: ['ADM_MANAGE_PRODUCTS'],
  integrations: ['ADM_VIEW_INTEGRATIONS'],
  integrations_manage: ['ADM_MANAGE_INTEGRATIONS'],
  rbac: ['ADM_VIEW_ACCESS_CONTROL'],
  rbac_manage: ['ADM_MANAGE_ACCESS_CONTROL'],
  audit_logs: ['ADM_VIEW_AUDIT_LOGS'],
  forwarding: ['ADM_VIEW_FORWARDING'],
  forwarding_manage: ['ADM_MANAGE_FORWARDING'],
  // Change Management permissions
  changes: ['ADM_VIEW_CHANGES', 'ADM_VIEW_DASHBOARD'],
  changes_create: ['ADM_CREATE_CHANGES', 'ADM_VIEW_CHANGES'],
  changes_edit: ['ADM_EDIT_CHANGES', 'ADM_VIEW_CHANGES'],
  changes_approve: ['ADM_APPROVE_CHANGES', 'ADM_VIEW_CHANGES'],
  // Only users with ADM_MANAGE_CHANGES should see/manage teams/config
  changes_manage: ['ADM_MANAGE_CHANGES'],
};

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [rbacData, setRbacData] = useState<RBACData | null>(null);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchRBACData = async () => {
    if (!user?.id || !isAuthenticated) {
      setRbacData(null);
      setLoading(false);
      return;
    }

    try {
      if (isFetchingRef.current) {
        return;
      }
      isFetchingRef.current = true;
      setLoading(true);
      // Abort any previous in-flight request
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch {}
      }
      abortControllerRef.current = new AbortController();
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/${user.id}/rbac?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store',
        signal: abortControllerRef.current.signal,
        keepalive: true
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.rbac) {
          setRbacData(data.rbac);
          // RBAC data loaded successfully
        } else {
          // Silently ignore malformed RBAC for performance
          setRbacData(null);
        }
      } else {
        // Avoid noisy logs; request failed
        setRbacData(null);
      }
    } catch (error) {
      setRbacData(null);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRBACData();
  }, [user?.id, isAuthenticated]);

  // Permission checking functions
  const hasPermission = (permissionCode: string): boolean => {
    if (!rbacData) return false;
    
    // Check both simple code (e.g., "ADM_VIEW_DASHBOARD") and full code (e.g., "ADMIN:ADM_VIEW_DASHBOARD")
    // Also check for GLOBAL application codes that map to ADMIN permissions
    return rbacData.permission_codes.some(code => 
      code === permissionCode || 
      code.endsWith(`:${permissionCode}`) ||
      code === `GLOBAL:${permissionCode}` ||
      code === `ADMIN:${permissionCode}`
    );
  };

  const hasRole = (roleCode: string): boolean => {
    if (!rbacData) return false;
    
    // Check both simple code and full code
    // Also check for GLOBAL roles that map to ADMIN roles
    return rbacData.role_codes.some(code => 
      code === roleCode || 
      code.endsWith(`:${roleCode}`) ||
      code === `GLOBAL:${roleCode}` ||
      code === `ADMIN:${roleCode}`
    );
  };

  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => hasPermission(code));
  };

  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    return permissionCodes.every(code => hasPermission(code));
  };

  const canAccess = (feature: string): boolean => {
    const requiredPermissions = FEATURE_PERMISSIONS[feature];
    if (!requiredPermissions) {
      console.warn(`Unknown feature: ${feature}`);
      return false;
    }
    
    return hasAnyPermission(requiredPermissions);
  };

  const refreshRBAC = useCallback(async () => {
    await fetchRBACData();
  }, [user?.id, isAuthenticated]);

  const contextValue: RBACContextType = {
    rbacData,
    loading,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    refreshRBAC
  };

  return (
    <RBACContext.Provider value={contextValue}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
};

// Utility components for conditional rendering
export const ProtectedComponent: React.FC<{
  permission?: string;
  feature?: string;
  role?: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ permission, feature, role, fallback = null, children }) => {
  const { hasPermission, hasRole, canAccess, loading } = useRBAC();

  if (loading) {
    return <>{fallback}</>;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (feature) {
    hasAccess = canAccess(feature);
  } else if (role) {
    hasAccess = hasRole(role);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default RBACContext;